import type { Settings } from './settings';

const KEY = 'brahm.active-sit.v1';

export interface ActiveSitMarker {
  startedAt: number;
  targetBucketMin: number;
  cadence: Settings['cadence'];
  interrupted: boolean;
  reason?: string;
  lastLifecycle?: string;
}

function store(): Storage | null {
  try {
    const value: unknown = typeof localStorage === 'undefined' ? null : localStorage;
    return value && typeof (value as Storage).getItem === 'function'
      && typeof (value as Storage).setItem === 'function'
      ? value as Storage
      : null;
  } catch {
    return null;
  }
}

function read(): ActiveSitMarker | null {
  try {
    const raw = store()?.getItem(KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<ActiveSitMarker>;
    if (typeof value.startedAt !== 'number' || typeof value.targetBucketMin !== 'number'
        || typeof value.cadence !== 'string') return null;
    return {
      startedAt: value.startedAt,
      targetBucketMin: value.targetBucketMin,
      cadence: value.cadence as Settings['cadence'],
      interrupted: value.interrupted === true,
      ...(typeof value.reason === 'string' ? { reason: value.reason } : {}),
      ...(typeof value.lastLifecycle === 'string'
        ? { lastLifecycle: value.lastLifecycle } : {}),
    };
  } catch {
    return null;
  }
}

function write(marker: ActiveSitMarker): void {
  try { store()?.setItem(KEY, JSON.stringify(marker)); } catch { /* diagnostic only */ }
}

/** Written before the start beacon: either both exist or the durable marker is stronger. */
export function startSitMarker(marker: Omit<ActiveSitMarker, 'interrupted'>): void {
  write({ ...marker, interrupted: false });
}

export function markSitInterrupted(reason: string): void {
  const marker = read();
  if (!marker) return;
  write({ ...marker, interrupted: true, reason });
}

export function noteSitLifecycle(lastLifecycle: string): void {
  const marker = read();
  if (!marker) return;
  write({ ...marker, lastLifecycle });
}

export function finishSitMarker(): void {
  try { store()?.removeItem(KEY); } catch { /* diagnostic only */ }
}

/**
 * A killed page has no final callback. The next page instance is therefore the only
 * reliable place to report that the start beacon had no matching end. Removing first
 * prevents a crashing startup from reporting the same sit on every reload.
 */
export function takeInterruptedSit(): ActiveSitMarker | null {
  const marker = read();
  finishSitMarker();
  return marker;
}
